import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';

const C = { rose: '#e3425c', creamDark: '#ebebed', dark: '#1c1c1e', white: '#ffffff' };

export default function ShippingPolicy() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.creamDark}`, padding: '14px 0' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to='/' style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: C.dark, fontSize: '14px', fontWeight: 500 }}>
            <FiArrowLeft size={16} />
            <img src="/ggr-logo-deck.png" alt='GGR' style={{ height: '50px', width: 'auto' }} />
          </Link>
          <Link to='/register' style={{ padding: '10px 22px', borderRadius: '8px', background: C.rose, color: C.white, textDecoration: 'none', fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
            Get Started
          </Link>
        </div>
      </nav>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
        <div className="card" style={{ padding: '40px' }}>
          <h1 style={{ fontSize: '28px', marginBottom: '8px' }}>Shipping & Delivery Policy</h1>
          <p style={{ color: 'var(--gray)', marginBottom: '24px', fontSize: '14px' }}>Last updated: June 2026</p>

          <h3>1. Overview</h3>
          <p>Grand Gather Registry ("GGR") is a marketplace connecting event hosts with service vendors. This Shipping & Delivery Policy outlines how delivery works for the different types of transactions on our platform. Because GGR hosts both <strong>service-based vendors</strong> (who perform services at events) and <strong>product-based vendors</strong> (who deliver physical goods), the delivery terms vary by vendor type.</p>

          <h3>2. Service Vendors (No Shipping Required)</h3>
          <p>The majority of vendors on GGR are <strong>service providers</strong> who attend events in person. These include:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
            <li>Caterers and food service providers</li>
            <li>Photographers and videographers</li>
            <li>DJs, musicians, and entertainers</li>
            <li>Decorators and event stylists</li>
            <li>Makeup artists, hair stylists, and beauty professionals</li>
            <li>Event planners and coordinators</li>
            <li>MC/hosts and speakers</li>
            <li>Security personnel</li>
            <li>Barmen and beverage service providers</li>
          </ul>
          <p>For service vendors, there is no physical shipping — the vendor travels to the host's event venue on the agreed date and time. Delivery details (venue address, arrival time, setup requirements) are communicated through the GGR platform's booking system and chat.</p>

          <h3>3. Product & Gift Vendors (Physical Delivery)</h3>
          <p>Some vendors on GGR sell physical products that require delivery. These include:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
            <li>Gift shops and gift registry items</li>
            <li>Cake and dessert deliveries</li>
            <li>Flower and decor deliveries</li>
            <li>Custom printed materials (invitations, programmes, signage)</li>
            <li>Party favours and event accessories</li>
          </ul>

          <h3>4. Delivery Methods</h3>
          <p>Physical product vendors on GGR may offer one or more of the following delivery methods, which are specified on their rate cards and communicated during the booking process:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
            <li><strong>Standard Delivery:</strong> Delivery to the address provided by the host within the vendor's service area. Delivery timeframes are set by each vendor based on product type and location.</li>
            <li><strong>Collection/Pickup:</strong> The host or buyer collects the item directly from the vendor's business location.</li>
            <li><strong>Event Day Delivery:</strong> The vendor delivers to the event venue on the event date, often included as part of the service.</li>
            <li><strong>Courier Delivery:</strong> The vendor may use a third-party courier service to deliver goods. Courier costs and timelines are communicated upfront.</li>
          </ul>

          <h3>5. Delivery Fees</h3>
          <p>Delivery fees (if applicable) are set by each vendor and displayed on their rate card or communicated before the booking is confirmed. Fees vary based on:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
            <li>Distance from the vendor's location to the delivery address</li>
            <li>Size and weight of the items</li>
            <li>Urgency of the delivery</li>
            <li>Whether setup or installation is included</li>
          </ul>
          <p>All delivery fees must be agreed upon before payment is made through the platform.</p>

          <h3>6. Delivery Timeframes</h3>
          <p>Delivery timeframes depend on the product type and are agreed upon between the host and vendor during the booking process. Typical timeframes include:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
            <li><strong>Custom orders (cakes, decor, printed materials):</strong> 3-14 days depending on complexity</li>
            <li><strong>Ready-made items (gifts, favours):</strong> 1-5 business days</li>
            <li><strong>Event day delivery:</strong> On the agreed event date at the specified time</li>
          </ul>

          <h3>7. Delivery Issues</h3>
          <p>If a delivery is delayed, damaged, or does not arrive:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
            <li>Contact the vendor directly through the GGR platform chat</li>
            <li>If the issue cannot be resolved with the vendor, file a dispute through the GGR platform</li>
            <li>GGR's dispute resolution team will review the case and facilitate a resolution</li>
            <li>For third-party courier deliveries, the vendor is responsible for liaising with the courier service</li>
          </ul>

          <h3>8. Vendor Responsibilities</h3>
          <p>Vendors offering physical delivery must:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
            <li>Clearly state delivery fees and timeframes before accepting a booking</li>
            <li>Pack items securely to prevent damage during transit</li>
            <li>Provide tracking information where available</li>
            <li>Deliver items that match the description and quality shown on their rate card</li>
            <li>Communicate any delays promptly to the host</li>
          </ul>

          <h3>9. Contact</h3>
          <p>For questions about delivery or to report a delivery issue:</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '16px', lineHeight: '1.8' }}>
            <li>Email: <a href="mailto:support@grandgatherregistry.co.za">support@grandgatherregistry.co.za</a></li>
            <li>WhatsApp: +27 61 391 1942</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
